import { CalendarDate } from '@internationalized/date';
import BigNumber from 'bignumber.js';
import { parseISO } from 'date-fns';
import { BookkeepingConfiguration } from '~/services/bookkeeping/bookkeeping-configuration';
import { Document, DocumentDirection, DocumentType, EInvoiceType } from '~/types/document';
import { TransactionRegistrationProposal } from '~/types/general-bookkeeping';
import { Money } from '~/types/money';
import { isDefined } from '~/types/typeGuards';
import { VATBook, VATBookType, VATCode, VATRegistrationEntry } from '~/types/vat-bookkeeping';
import { areEntriesBalanced } from '~/utils/bookkeeping/balancing';
import { proposalEntriesToFormEntries } from '~/utils/bookkeeping/bookkeeping-entry';
import { BookkeepingTransactionSchema } from '~/views/bookkeeping/transactions/BookkeepingTransactionSchema';

export const entryDeductibleVAT = (entry: VATRegistrationEntry): Money => {
  return {
    amount: BigNumber(entry.taxAmount.amount)
      .times(entry.vatCode.vatDeductibility ?? 100)
      .dividedBy(100)
      .toFixed(),
    currency: entry.taxAmount.currency,
  };
};

export const documentDirectionForBookType = (
  bookType: VATBookType.Sales | VATBookType.Purchases,
): DocumentDirection => {
  return bookType === VATBookType.Sales ? DocumentDirection.issued : DocumentDirection.received;
};

export const vatBookForDirection = (direction: DocumentDirection) => {
  return direction === DocumentDirection.issued ? VATBookType.Sales : VATBookType.Purchases;
};

export const vatBookForDocument = ({
  document,
  reverseCharge,
}: {
  document: Document;
  reverseCharge?: boolean;
}): VATBookType => {
  if (document.documentType === DocumentType.SelfInvoice || reverseCharge) {
    return VATBookType.Sales;
  }
  if (document.documentType === DocumentType.Bill) {
    return VATBookType.Bills;
  }
  return vatBookForDirection(document.direction);
};

// TD27 (autoconsumo) is a sales-only self-invoice.
export const isSalesOnlySelfInvoice = (eInvoiceType: EInvoiceType | null | undefined): boolean =>
  eInvoiceType === EInvoiceType.AutofatturaPerAutoconsumo;

export const secondaryVatBookForDocument = ({
  document,
  reverseCharge,
  eInvoiceType,
}: {
  document: Document;
  reverseCharge?: boolean;
  eInvoiceType?: EInvoiceType | null;
}): VATBookType | null => {
  if (isSalesOnlySelfInvoice(eInvoiceType)) {
    return null;
  }
  if (document.documentType === DocumentType.SelfInvoice || reverseCharge) {
    return VATBookType.Purchases;
  }
  return null;
};

// A VAT code drives a reverse-charge (integrazione) sales-book entry when it is flagged `reverseChargeIntegration`.
export const isReverseChargeVatCode = (vatCode: VATCode | null | undefined): boolean =>
  isDefined(vatCode) && vatCode.reverseChargeIntegration;

export function getRelevantBooks(books: VATBook[], bookType: VATBookType, year: number) {
  return getRelevantBooksAnd(books, bookType, year, null);
}

export function getRelevantBooksAnd(
  books: VATBook[],
  bookType: VATBookType,
  year: number,
  bookId: VATBook['id'] | null,
) {
  return books.filter((book) => (book.bookType === bookType && book.year === year) || (bookId && book.id === bookId));
}

export const bookToOption = (book: VATBook): { label: string; value: string; description: string } => ({
  label: book.name,
  value: book.id,
  description: `${book.year} - ${book.bookType}`,
});

export const documentInitialDate = (document: Document): CalendarDate => {
  let date: Date = parseISO(document.creationDate);
  if (
    document.direction === DocumentDirection.received &&
    document.documentType !== DocumentType.SelfInvoice &&
    (document.acceptanceDate || document.deliveryDate)
  ) {
    if (document.acceptanceDate) {
      date = parseISO(document.acceptanceDate);
    } else if (document.deliveryDate) {
      date = parseISO(document.deliveryDate);
    }
  }

  return new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
};

export const fromTransactionProposalToFormValues = (proposal: TransactionRegistrationProposal) => {
  return {
    date: proposal.date,
    description: proposal.description,
    entries: proposalEntriesToFormEntries(proposal.entries),
    recommendedReconciliationFlowId: proposal.recommendedFlowId,
  };
};

export const isTransactionRegistrationProposalValid = (
  registrationProposal: TransactionRegistrationProposal | undefined,
  bookkeepingConfiguration: BookkeepingConfiguration,
): boolean => {
  if (!isDefined(registrationProposal)) {
    return false;
  }

  const formValues = fromTransactionProposalToFormValues(registrationProposal);

  if (!BookkeepingTransactionSchema.safeParse(formValues).success || !areEntriesBalanced(formValues.entries)) {
    return false;
  }

  const { debtsSuppliers, creditsClients } = bookkeepingConfiguration.accounts;

  // Entries on Debts with Suppliers or Credits with Clients require both a counterpart and a document
  return formValues.entries.every((entry) => {
    const isCounterpartAccount =
      (debtsSuppliers !== null && entry.bookkeepingAccountId === debtsSuppliers) ||
      (creditsClients !== null && entry.bookkeepingAccountId === creditsClients);

    if (!isCounterpartAccount) {
      return true;
    }

    return isDefined(entry.counterpart) && isDefined(entry.documentId);
  });
};
